import React from 'react';

import type * as api from '@gitbook/api-client';
import type { InlineButtonData } from '@gitbook/doc-core';
import { Icon as ContentIcon, type IconName } from '@gitbook/icons';
import type { Inline } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    FieldGroup,
    Fieldset,
    Group,
    Heading,
    IconPicker,
    Label,
    Paper,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    SegmentedControl,
    type SegmentedControlOption,
    TextInput,
    usePopoverClose,
} from '@gitbook/spine-react';

import { ContentReferenceInput } from '../../ContentReferences';
import { DocumentationLink } from '../../Link';

const KINDS: SegmentedControlOption<api.DocumentInlineButton['data']['kind']>[] = [
    { label: 'Primary', value: 'primary' },
    { label: 'Secondary', value: 'secondary' },
];

/**
 * Body of a popover to edit a button.
 */
export function EditButtonDialogPopover(props: { node: Inline; editor: EditorContainer }) {
    const { node, editor } = props;
    const data = node.data.toFrozenObject() as InlineButtonData;

    const closePopover = usePopoverClose();
    const [iconQuery, setIconQuery] = React.useState('');

    return (
        <Paper className="w-[430px] p-4">
            <Fieldset>
                <FieldGroup>
                    <Label>Label</Label>
                    <div className="flex items-center gap-2">
                        <PopoverRoot>
                            <PopoverTrigger>
                                <Button
                                    label={data.icon ? 'Change icon' : 'Add icon'}
                                    iconOnly
                                    kind="secondary"
                                    size="large"
                                    leadingIcon={
                                        data.icon ? (
                                            <ContentIcon icon={data.icon as IconName} />
                                        ) : (
                                            Icon.AddNew
                                        )
                                    }
                                    aria-description={
                                        data.icon ? `Selected icon: ${data.icon}` : undefined
                                    }
                                />
                            </PopoverTrigger>
                            <Popover>
                                <Paper className="flex flex-col">
                                    <Group
                                        orientation="horizontal"
                                        className="min-h-8 w-full justify-between p-2"
                                    >
                                        <Heading>Pick an icon</Heading>
                                        {data.icon ? (
                                            <Button
                                                onPress={() => {
                                                    editor.change((change) => {
                                                        change.setNodeByKey(node.key, {
                                                            data: { ...data, icon: undefined },
                                                        });
                                                    });
                                                }}
                                                label="Remove"
                                                size="medium"
                                                kind="blank"
                                            />
                                        ) : null}
                                    </Group>
                                    <IconPicker
                                        onPick={(icon) => {
                                            editor.change((change) => {
                                                change.setNodeByKey(node.key, {
                                                    data: { ...data, icon },
                                                });
                                            });
                                            closePopover();
                                        }}
                                        query={iconQuery}
                                        onChangeQuery={setIconQuery}
                                    />
                                </Paper>
                            </Popover>
                        </PopoverRoot>
                        <TextInput
                            value={data.label}
                            onChange={(event) => {
                                editor.change((change) => {
                                    change.setDataByKey(node.key, ['label'], event.target.value);
                                });
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    closePopover();
                                }
                            }}
                        />
                    </div>
                </FieldGroup>
                <FieldGroup orientation="horizontal">
                    <Group>
                        <Label>Variant</Label>
                        <Description>
                            The button will use your site's{' '}
                            <DocumentationLink path="publishing-documentation/customization">
                                customization settings
                            </DocumentationLink>
                            .
                        </Description>
                    </Group>
                    <SegmentedControl
                        options={KINDS}
                        active={data.kind}
                        onChange={(value) => {
                            editor.change((change) => {
                                change.setDataByKey(node.key, ['kind'], value);
                            });
                        }}
                    />
                </FieldGroup>
                <FieldGroup>
                    <Label>On click</Label>
                    <Description>
                        Your button can point to either a URL, or a page or space in GitBook.
                    </Description>

                    <ContentReferenceInput
                        accept={['url', 'anchor', 'page', 'space']}
                        placeholder="Search for a page or type a URL..."
                        value={data.ref}
                        onChangeValue={(value) => {
                            if (value) {
                                editor.change((change) => {
                                    change.setDataByKey(node.key, ['ref'], value);
                                });
                            }
                        }}
                        size="medium"
                        clearable={false}
                    />
                </FieldGroup>
            </Fieldset>
        </Paper>
    );
}
